import {Tile} from "@game/map/tile";
import {CSSProperties} from "react";
import {TileView} from "@components/interface/TileView";
import {Game} from "@game/game";
import {PlacedTileController} from "@components/map/PlacedTileController";
import {connect} from "react-redux";
import {selectDebug} from "@redux/selectors/debug";
import {IDebug} from "@redux/slices/debug";
import {TILE_SIZE} from "@components/core/theme";
import {PlaceView} from "@components/map/PlaceView";
import {Vector2} from "@utility/vector2";
import {GameEvent} from "@game/gameEvent";

// Parchment-toned biome styles: muted base + CSS pattern to evoke terrain
const BIOME_STYLES: Record<string, CSSProperties> = {
    desert: {
        backgroundColor: "#f0e5cc",
        backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(160,120,60,0.09) 3px, rgba(160,120,60,0.09) 4px)",
    },
    forest: {
        backgroundColor: "#e4e8d8",
        backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(50,90,50,0.11) 4px, rgba(50,90,50,0.11) 5px)",
    },
    grasslands: {
        backgroundColor: "#eaecdc",
        backgroundImage: "repeating-linear-gradient(90deg, transparent, transparent 6px, rgba(80,120,50,0.08) 6px, rgba(80,120,50,0.08) 7px)",
    },
    hills: {
        backgroundColor: "#ebe0c5",
        backgroundImage: [
            "repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(120,95,50,0.11) 3px, rgba(120,95,50,0.11) 4px)",
            "repeating-linear-gradient( 45deg, transparent, transparent 3px, rgba(120,95,50,0.06) 3px, rgba(120,95,50,0.06) 4px)",
        ].join(", "),
    },
    ice: {
        backgroundColor: "#edf0f5",
        backgroundImage: "repeating-linear-gradient(60deg, transparent, transparent 5px, rgba(150,175,215,0.12) 5px, rgba(150,175,215,0.12) 6px)",
    },
    jungle: {
        backgroundColor: "#e2e8d2",
        backgroundImage: [
            "repeating-linear-gradient( 45deg, transparent, transparent 2px, rgba(40,80,40,0.1) 2px, rgba(40,80,40,0.1) 3px)",
            "repeating-linear-gradient(-45deg, transparent, transparent 2px, rgba(40,80,40,0.1) 2px, rgba(40,80,40,0.1) 3px)",
        ].join(", "),
    },
    savannah: {
        backgroundColor: "#f0e5cc",
        backgroundImage: "radial-gradient(circle, rgba(160,120,50,0.12) 1px, transparent 1px)",
        backgroundSize: "6px 6px",
    },
    tundra: {
        backgroundColor: "#e8ecf0",
        backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 5px, rgba(120,145,170,0.1) 5px, rgba(120,145,170,0.1) 6px)",
    },
};

// ─── TileBiome ───────────────────────────────────────────────────────────────

interface ITileBiome {
    debug?: IDebug
    tile: Tile
}

function TileBiomeInner({debug, tile}: ITileBiome) {
    const biomeStyle = BIOME_STYLES[tile.biome] ?? { backgroundColor: "#f5f0e8" };
    const left = Math.floor(tile.position.x * TILE_SIZE.x);
    const top = Math.floor(tile.position.y * TILE_SIZE.y);
    const style: CSSProperties = {
        border: "1px solid white",
        position: "absolute",
        height: TILE_SIZE.y,
        width: TILE_SIZE.x,
        userSelect: "none",
        left,
        top,
        ...biomeStyle,
    };

    return (
        <div style={style}>
            {debug?.showTileImage &&
                <img alt="terrain" draggable={false} style={{}} src={`/images/terrain/${tile.groundImage}.png`}/>}
            {debug?.showAltitude &&
                <span style={{position: "absolute", bottom: 1, right: 2, fontSize: 9, color: "white",
                    fontFamily: "monospace", textShadow: "0 0 2px #000", pointerEvents: "none"}}>
                    {tile.altitude.toFixed(2)}
                </span>}
        </div>
    );
}

const tileBiomeMapState = (store: never) => ({ debug: selectDebug(store) });
const TileBiome = connect(tileBiomeMapState, {})(TileBiomeInner);
export { TileBiome };

// ─── TileBlock ───────────────────────────────────────────────────────────────

interface ITileBlock {
    debug?: IDebug
    tile: Tile
}

function TileBlockInner({debug, tile}: ITileBlock) {
    if (!tile.place || tile.letter) return null;
    const left = Math.floor(tile.position.x * TILE_SIZE.x);
    const top = Math.floor(tile.position.y * TILE_SIZE.y);
    const style: CSSProperties = {
        position: "absolute",
        left,
        top,
        width: TILE_SIZE.x,
        height: TILE_SIZE.y,
        display: "flex",
        flexDirection: "column",
    };

    return (
        <div style={style}>
            <PlaceView debug={debug} tile={tile}/>
        </div>
    );
}

const tileBlockMapState = (store: never) => ({ debug: selectDebug(store) });
const TileBlock = connect(tileBlockMapState, {})(TileBlockInner);
export { TileBlock };

// ─── TileLetter ──────────────────────────────────────────────────────────────

const shovellingDisabledStyle: CSSProperties = {
    opacity: 0.4,
    pointerEvents: "none"
};

interface ITileLetter {
    game: Game
    hiddenTilePos?: Vector2
    shovelling: boolean
    tile: Tile
}

export function TileLetter({game, hiddenTilePos, shovelling, tile}: ITileLetter) {
    if (tile.letter === undefined) return null;
    const isAnimating = hiddenTilePos?.equals(tile.position) ?? false;
    if (isAnimating) return null;

    const left = Math.floor(tile.position.x * TILE_SIZE.x);
    const top = Math.floor(tile.position.y * TILE_SIZE.y);

    const handleShovelled = () => {
        if (shovelling && tile.letter) {
            game.events.update(new GameEvent("ShovellingTile", { position: tile.position, letter: tile.letter }));
            game.shovelTile(tile.location).catch(() => {
                game.events.update(new GameEvent("ShovelFailed", { position: tile.position }));
            });
        }
    };

    const wrapperStyle: CSSProperties = {
        position: "absolute",
        left,
        top,
        width: TILE_SIZE.x,
        height: TILE_SIZE.y,
    };

    let contents = null;
    if (tile.letter.placed === undefined) {
        contents = <PlacedTileController game={game} shovelling={shovelling} tile={tile}/>;
    } else {
        const innerStyle = (shovelling && tile.place?.type === "bonus") ? shovellingDisabledStyle : {};
        contents = (
            <div style={innerStyle}>
                <TileView dragging={false} letter={tile.letter} onClick={handleShovelled}/>
            </div>
        );
    }

    return (
        <div style={wrapperStyle}>
            {contents}
        </div>
    );
}
